import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { client } from '@/api/client'
import { AuthLayout } from '@/components/AuthLayout'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { CheckCircle } from 'lucide-react'

interface ResetPasswordForm {
  email: string
  confirmEmail: string
}

interface RateLimitError {
  rate_limited: boolean
  minutes_remaining: number
  message: string
}

export function ResetPasswordPage() {
  const { t } = useTranslation('auth')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')
  const [rateLimit, setRateLimit] = useState<RateLimitError | null>(null)

  const { register, handleSubmit, formState: { errors }, getValues } = useForm<ResetPasswordForm>()

  const onSubmit = async (data: ResetPasswordForm) => {
    setIsSubmitting(true)
    setError('')
    setRateLimit(null)

    try {
      await client.post('/auth/forgot-password/', { email: data.email })
      setSuccess(true)
    } catch (err: any) {
      if (err.response?.status === 429 && err.response?.data?.rate_limited) {
        setRateLimit({
          rate_limited: true,
          minutes_remaining: err.response.data.minutes_remaining,
          message: err.response.data.message
        })
      } else {
        setError(err.response?.data?.message || t('resetPassword.error'))
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  if (success) {
    return (
      <AuthLayout>
        <div className="flex justify-center mb-8">
          <img src="/images/logo.png" alt="The Ranch" className="h-20 w-auto" />
        </div>
        <Card>
          <CardContent className="py-8 text-center space-y-4">
            <div className="rounded-full bg-emerald-100 p-3 w-fit mx-auto">
              <CheckCircle className="h-8 w-8 text-emerald-600" />
            </div>
            <h2 className="text-xl font-semibold">{t('resetPassword.successTitle')}</h2>
            <p className="text-muted-foreground text-sm">{t('resetPassword.successMessage')}</p>
            <Button asChild>
              <Link to="/login">{t('resetPassword.backToLogin')}</Link>
            </Button>
          </CardContent>
        </Card>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout>
      <div className="flex justify-center mb-8">
        <img src="/images/logo.png" alt="The Ranch" className="h-20 w-auto" />
      </div>

      <Card>
        <CardHeader className="text-center">
          <CardTitle className="text-xl">{t('resetPassword.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
            {error && (
              <div className="rounded-md bg-destructive/10 border border-destructive/30 p-3">
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            {rateLimit && (
              <div className="rounded-md bg-amber-50 border border-amber-200 p-3">
                <p className="text-sm text-amber-800">
                  {t('resetPassword.rateLimitedWithTime', { minutes: rateLimit.minutes_remaining })}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email-input">{t('resetPassword.emailLabel')}</Label>
              <Input
                id="email-input"
                {...register('email', {
                  required: t('resetPassword.emailRequired'),
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: t('resetPassword.emailInvalid')
                  }
                })}
                type="email"
                placeholder={t('resetPassword.emailPlaceholder')}
              />
              {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-email-input">{t('resetPassword.confirmEmailLabel')}</Label>
              <Input
                id="confirm-email-input"
                {...register('confirmEmail', {
                  required: t('resetPassword.confirmEmailRequired'),
                  validate: value => value === getValues('email') || t('resetPassword.emailMismatch')
                })}
                type="email"
                placeholder={t('resetPassword.confirmEmailPlaceholder')}
              />
              {errors.confirmEmail && <p className="text-xs text-destructive">{errors.confirmEmail.message}</p>}
            </div>

            <div className="space-y-3">
              <Button type="submit" className="w-full" disabled={isSubmitting || !!rateLimit}>
                {isSubmitting ? t('resetPassword.submitting') : t('resetPassword.submitButton')}
              </Button>
              <div className="text-center">
                <Link to="/login" className="text-sm text-primary hover:underline">
                  {t('resetPassword.cancel')}
                </Link>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
    </AuthLayout>
  )
}
